import { interactiveRegistry, isPointInBounds } from './registry'
import type { Bounds } from './use-interactive'

const POINTER_HEARTBEAT_MS = 500

export interface InteractiveFrame {
  seq: number
  bounds: Record<string, Bounds>
  inside: boolean
  pointerActivity: boolean
  /** The frame was emitted by a bounds measurement, not pointer activity or an override. */
  boundsChanged: boolean
}

class InteractiveFramePublisher {
  // Keep ordering monotonic across renderer HMR/module reloads as well as
  // ordinary publishes. Millisecond epoch leaves ample safe-integer room for
  // the per-frame increments.
  private seq = Date.now() * 1000
  private bounds: Record<string, Bounds> = {}
  private lastPointer: { x: number; y: number } | null = null
  private lastInside = false
  private lastPointerPublishAt = 0

  publishBounds(bounds: Record<string, Bounds>): void {
    this.bounds = bounds
    this.lastInside = this.hitTestLastPointer()
    this.publish(this.lastInside, false, true)
  }

  publishPointer(x: number, y: number): void {
    this.lastPointer = { x, y }
    const inside = this.hitTestLastPointer()
    const now = Date.now()
    if (inside === this.lastInside && now - this.lastPointerPublishAt < POINTER_HEARTBEAT_MS) {
      return
    }

    this.lastInside = inside
    this.lastPointerPublishAt = now
    this.publish(inside, true, false)
  }

  publishOverride(inside: boolean): void {
    this.lastInside = inside
    this.publish(inside, false, false)
  }

  private hitTestLastPointer(): boolean {
    const pointer = this.lastPointer
    if (!pointer) {
      return false
    }
    return Object.values(interactiveRegistry.lastMeasured).some((bounds) =>
      isPointInBounds(bounds, pointer.x, pointer.y),
    )
  }

  private publish(inside: boolean, pointerActivity: boolean, boundsChanged: boolean): void {
    window.ipc?.publishInteractiveFrame({
      seq: ++this.seq,
      bounds: this.bounds,
      inside,
      pointerActivity,
      boundsChanged,
    })
  }
}

export const interactiveFramePublisher = new InteractiveFramePublisher()

export function setOverlayInteractive(isInteractive: boolean): void {
  interactiveFramePublisher.publishOverride(isInteractive)
}
